import { RoadmapProject, TimeHorizon, ProgramGroupData, HorizonData, KpiCounts, QuarterInfo, QuarterFilter } from '../types';
import { getQuarterLabel, getStatusColor, formatCompactCurrency } from '../../TaskPrioritization/utils/taskUtils';

// ─── Status helpers ──────────────────────────────────────────────────────────

/**
 * Normalise a raw status string into a CSS-safe class name.
 *
 * Status set (8 values + blank):
 *   In Progress · Blocked · Delayed · In Review · Not Started · On Hold · Completed · Cancelled
 */
export const getStatusClass = (status: string): string => {
  const s = (status || '').toLowerCase().trim();
  if (s.includes('in progress')) return 'in-progress';
  if (s.includes('blocked'))     return 'blocked';
  if (s.includes('delayed'))     return 'delayed';
  if (s.includes('in review'))   return 'in-review';
  if (s.includes('not started')) return 'not-started';
  if (s.includes('on hold'))     return 'on-hold';
  if (s.includes('complet') || s === 'done') return 'complete';
  if (s.includes('cancel'))      return 'cancelled';
  return 'no-status'; // blank / not yet triaged
};

/** Returns a human-readable label for a status string. */
export const getStatusLabel = (status: string): string => {
  const s = (status || '').toLowerCase().trim();
  if (s.includes('in progress')) return 'In Progress';
  if (s.includes('blocked'))     return 'Blocked';
  if (s.includes('delayed'))     return 'Delayed';
  if (s.includes('in review'))   return 'In Review';
  if (s.includes('not started')) return 'Not Started';
  if (s.includes('on hold'))     return 'On Hold';
  if (s.includes('complet') || s === 'done') return 'Completed';
  if (s.includes('cancel'))      return 'Cancelled';
  return status || '—'; // blank / not yet triaged
};

/** Returns the CSS class for a timeline bar based on status. */
export const getTimelineBarClass = (status: string): string => {
  const s = (status || '').toLowerCase().trim();
  if (s.includes('in progress')) return 'status-in-progress';
  if (s.includes('blocked'))     return 'status-blocked';
  if (s.includes('delayed'))     return 'status-delayed';
  if (s.includes('in review'))   return 'status-in-review';
  if (s.includes('not started')) return 'status-not-started';
  if (s.includes('on hold'))     return 'status-on-hold';
  if (s.includes('complet') || s === 'done') return 'status-complete';
  if (s.includes('cancel'))      return 'status-cancelled';
  return 'status-no-status';
};

// ─── KPI counts ──────────────────────────────────────────────────────────────

export const getKpiCounts = (projects: RoadmapProject[]): KpiCounts => {
  let active = 0;    // NOW: In Progress + Blocked + Delayed
  let queued = 0;    // NEXT: Not Started + In Review
  let completed = 0; // COMPLETE: Completed + Cancelled
  let onHold = 0;    // LATER: On Hold

  projects.forEach(p => {
    const s = (p.status || '').toLowerCase().trim();
    if (s.includes('in progress') || s.includes('blocked') || s.includes('delayed')) {
      active++;
    } else if (s.includes('not started') || s.includes('in review')) {
      queued++;
    } else if (s.includes('complet') || s === 'done' || s.includes('cancel')) {
      completed++;
    } else if (s.includes('on hold')) {
      onHold++;
    }
    // blank / no status → not counted in any KPI card
  });

  return { active, queued, completed, onHold };
};

// ─── Quarter utilities ────────────────────────────────────────────────────────

/** Build a QuarterInfo from parts. */
const makeQuarterInfo = (quarterNum: 1 | 2 | 3 | 4, year: number): QuarterInfo => {
  const quarter = `Q${quarterNum}` as 'Q1' | 'Q2' | 'Q3' | 'Q4';
  return { key: `${quarter} ${year}`, quarter, year, sortKey: year * 10 + quarterNum };
};

/** Derive quarter from a 1-indexed month number (1-3=Q1, 4-6=Q2, 7-9=Q3, 10-12=Q4). */
const monthToQuarter = (month1: number): 1 | 2 | 3 | 4 => {
  if (month1 <= 3) return 1;
  if (month1 <= 6) return 2;
  if (month1 <= 9) return 3;
  return 4;
};

/**
 * Get the QuarterInfo for a project based on its launch_date.
 * Handles both "YYYY-MM-DD" and full ISO timestamps "YYYY-MM-DDTHH:mm:ss.sssZ".
 * Quarter is derived from the calendar month: 1-3=Q1, 4-6=Q2, 7-9=Q3, 10-12=Q4.
 * Always extracts the date portion and parses as LOCAL time to avoid UTC midnight shift.
 */
export const getProjectQuarterInfo = (project: RoadmapProject): QuarterInfo | null => {
  const raw = project.launch_date;
  if (!raw) {
    console.debug(`[Roadmap] getProjectQuarterInfo: no launch_date for "${project.name}" (gid=${project.gid})`);
    return null;
  }

  const s = raw.trim();
  if (!s) {
    console.debug(`[Roadmap] getProjectQuarterInfo: empty launch_date for "${project.name}"`);
    return null;
  }

  const datePart = s.includes('T') ? s.split('T')[0] : s;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? `${datePart}T00:00:00` : datePart;
  const d = new Date(normalized);
  if (isNaN(d.getTime())) {
    console.warn(`[Roadmap] getProjectQuarterInfo: invalid date "${raw}" for "${project.name}" — normalized="${normalized}"`);
    return null;
  }

  const month1 = d.getMonth() + 1;
  const year = d.getFullYear();
  const qi = makeQuarterInfo(monthToQuarter(month1), year);
  console.debug(`[Roadmap] getProjectQuarterInfo: "${project.name}" launch_date="${raw}" → ${qi.key}`);
  return qi;
};

/** Parse a date/quarter string into a QuarterInfo (used for current-quarter calculation). */
export const parseQuarterFromDate = (dateStr: string): QuarterInfo | null => {
  if (!dateStr) return null;
  const s = dateStr.trim();
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s;
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return null;
  const month1 = d.getMonth() + 1;
  return makeQuarterInfo(monthToQuarter(month1), d.getFullYear());
};

/** Get the current quarter info for today (uses local date). */
export const getCurrentQuarterInfo = (): QuarterInfo => {
  const now = new Date();
  const month1 = now.getMonth() + 1;
  const year = now.getFullYear();
  return makeQuarterInfo(monthToQuarter(month1), year);
};

/** Get all unique quarters present in a project list, sorted chronologically. */
export const getAllQuarters = (projects: RoadmapProject[]): QuarterInfo[] => {
  const map = new Map<string, QuarterInfo>();
  projects.forEach(p => {
    const qi = getProjectQuarterInfo(p);
    if (qi && !map.has(qi.key)) map.set(qi.key, qi);
  });
  const result = Array.from(map.values()).sort((a, b) => a.sortKey - b.sortKey);
  console.log(
    `[Roadmap] getAllQuarters: ${projects.length} projects → ${result.length} quarters: [${result.map(q => q.key).join(', ')}]`,
    `| ${projects.filter(p => getProjectQuarterInfo(p)).length} have a valid launch_date`
  );
  return result;
};

/** Filter projects by a QuarterFilter pill selection. */
export const filterByQuarter = (projects: RoadmapProject[], quarterFilter: QuarterFilter): RoadmapProject[] => {
  if (quarterFilter === 'all') return projects;
  return projects.filter(p => {
    const qi = getProjectQuarterInfo(p);
    return qi?.quarter === quarterFilter;
  });
};

/** Filter projects by a KPI status category. */
export const filterByKpiStatus = (
  projects: RoadmapProject[],
  kpiStatus: 'active' | 'queued' | 'completed' | 'onHold' | null
): RoadmapProject[] => {
  if (!kpiStatus) return projects;
  return projects.filter(p => {
    const s = (p.status || '').toLowerCase().trim();
    switch (kpiStatus) {
      case 'active':    return s.includes('in progress') || s.includes('blocked') || s.includes('delayed');
      case 'queued':    return s.includes('not started') || s.includes('in review');
      case 'completed': return s.includes('complet') || s === 'done' || s.includes('cancel');
      case 'onHold':    return s.includes('on hold');
      default: return true;
    }
  });
};

// ─── Horizon logic ────────────────────────────────────────────────────────────

export const getProjectHorizon = (project: RoadmapProject): TimeHorizon => {
  const s = (project.status || '').toLowerCase().trim();
  if (s.includes('in progress') || s.includes('blocked') || s.includes('delayed')) return 'NOW';
  if (s.includes('not started') || s.includes('in review')) return 'NEXT';
  if (s.includes('complet') || s === 'done' || s.includes('cancel')) return 'COMPLETE';
  return 'LATER'; // On Hold + blank
};

// ─── Program grouping ─────────────────────────────────────────────────────────

export const groupByProgram = (projects: RoadmapProject[]): ProgramGroupData[] => {
  const programMap = new Map<string, RoadmapProject[]>();

  projects.forEach(project => {
    const program = project.program || 'Unassigned';
    if (!programMap.has(program)) programMap.set(program, []);
    programMap.get(program)!.push(project);
  });

  const programGroups: ProgramGroupData[] = [];
  programMap.forEach((projs, programName) => {
    programGroups.push({
      programName,
      projects: projs,
      totalEntitlement: projs.reduce((sum, p) => sum + (p.annualized_entitlement || 0), 0),
      totalSDE: projs.reduce((sum, p) => sum + (p.hc_sde || 0), 0),
      projectCount: projs.length,
    });
  });

  return programGroups.sort((a, b) => b.totalEntitlement - a.totalEntitlement);
};

export const organizeByHorizon = (projects: RoadmapProject[]): HorizonData[] => {
  const horizonMap: Record<TimeHorizon, RoadmapProject[]> = {
    NOW: [],
    NEXT: [],
    LATER: [],
    COMPLETE: [],
  };

  projects.forEach(project => {
    horizonMap[getProjectHorizon(project)].push(project);
  });

  return (['NOW', 'NEXT', 'LATER', 'COMPLETE'] as TimeHorizon[]).map(horizon => {
    const horizonProjects = horizonMap[horizon];
    const programs = groupByProgram(horizonProjects);
    return {
      horizon,
      programs,
      totalProjects: horizonProjects.length,
      totalEntitlement: horizonProjects.reduce((sum, p) => sum + (p.annualized_entitlement || 0), 0),
      totalSDE: horizonProjects.reduce((sum, p) => sum + (p.hc_sde || 0), 0),
    };
  });
};

// ─── Filter helpers ───────────────────────────────────────────────────────────

export const applyRoadmapFilters = (
  projects: RoadmapProject[],
  filters: {
    programs: string[];
    statuses: string[];
    l7Managers: string[];
    themes: string[];
    assignees: string[];
    opDecisions: string[];
    missionFits: string[];
    targetYears: string[];
    requestTypes: string[];
    searchQuery: string;
  }
): RoadmapProject[] => {
  return projects.filter(project => {
    if (filters.programs.length > 0 && !filters.programs.includes(project.program || '')) return false;
    if (filters.statuses.length > 0 && !filters.statuses.includes(project.status || '')) return false;
    if (filters.l7Managers.length > 0 && !filters.l7Managers.includes(project.l7_manager || '')) return false;
    if (filters.themes.length > 0 && !filters.themes.includes(project.theme || '')) return false;
    if (filters.assignees.length > 0 && !filters.assignees.includes(project.assignee?.name || '')) return false;
    if (filters.opDecisions.length > 0 && !filters.opDecisions.includes(project.op_decision || '')) return false;
    if (filters.missionFits.length > 0 && !filters.missionFits.includes(project.mission_fit || '')) return false;
    if (filters.targetYears.length > 0 && !filters.targetYears.includes(project.target_year || '')) return false;
    if (filters.requestTypes.length > 0 && !filters.requestTypes.includes(project.request_type || '')) return false;

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const searchableText = [
        project.name,
        project.key_deliverables,
        project.problem_statement,
        project.program,
        project.theme,
      ]
        .join(' ')
        .toLowerCase();
      if (!searchableText.includes(query)) return false;
    }

    return true;
  });
};

export const getUniquePrograms = (projects: RoadmapProject[]): string[] =>
  Array.from(new Set(projects.map(p => p.program).filter(Boolean))).sort();

export const getUniqueStatuses = (projects: RoadmapProject[]): string[] =>
  Array.from(new Set(projects.map(p => p.status).filter(Boolean))).sort();

export const getUniqueL7Managers = (projects: RoadmapProject[]): string[] =>
  Array.from(new Set(projects.map(p => p.l7_manager).filter(Boolean))).sort();

export const getUniqueThemes = (projects: RoadmapProject[]): string[] =>
  Array.from(new Set(projects.map(p => p.theme).filter(Boolean))).sort();

export const getUniqueAssignees = (projects: RoadmapProject[]): string[] =>
  Array.from(new Set(projects.map(p => p.assignee?.name).filter(Boolean) as string[])).sort();

export const getUniqueOpDecisions = (projects: RoadmapProject[]): string[] =>
  Array.from(new Set(projects.map(p => p.op_decision).filter(Boolean))).sort();

export const getUniqueMissionFits = (projects: RoadmapProject[]): string[] =>
  Array.from(new Set(projects.map(p => p.mission_fit).filter(Boolean))).sort();

export const getUniqueTargetYears = (projects: RoadmapProject[]): string[] =>
  Array.from(new Set(projects.map(p => p.target_year).filter(Boolean))).sort();

export const getUniqueRequestTypes = (projects: RoadmapProject[]): string[] =>
  Array.from(new Set(projects.map(p => p.request_type).filter(Boolean))).sort();

// ─── Assignee avatar helpers ──────────────────────────────────────────────────

/** Get initials from a full name string (up to 2 chars). */
export const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// ─── Re-exports ───────────────────────────────────────────────────────────────

export { getQuarterLabel, getStatusColor, formatCompactCurrency };